/**
 * BrandReveal: the full-screen loader. Spec: `brand-redesign-docs/09-LOADER.md`.
 *
 * `launch`: dark hero field in both themes (app start, auth boot). The mark
 *   rises in over a slowly drifting mint glow, a sand segment travels along a
 *   thin line (indeterminate: there is no real progress figure), and the sand
 *   tag chip reads "<name> Workspace". `leaving` fades it out with a CSS
 *   transition while `LaunchCurtain` unmounts it on a timer.
 * `route`: theme-aware canvas (route changes, external redirect). A smaller
 *   mark inside a teal-to-sand orbit ring. No visible text.
 *
 * Reduced motion collapses both to still versions.
 */

import { motion, useReducedMotion } from "framer-motion";

import { useTenant } from "@/lib/tenant";
import { TagChip } from "@/primitives/brand";

import { LogoMark } from "./LogoMark";
import "./brandReveal.css";

const EASE = [0.22, 1, 0.36, 1] as const;

export interface BrandRevealProps {
  /** `launch` for app start and auth boot, `route` (default) for the rest. */
  variant?: "launch" | "route";
  /** Accessible name. Defaults per variant. */
  label?: string;
  /** `launch` only: fade out and stop catching clicks while it unmounts. */
  leaving?: boolean;
}

export function BrandReveal({ variant = "route", label, leaving = false }: BrandRevealProps) {
  const reduce = useReducedMotion();
  const tenant = useTenant();

  if (variant === "launch") {
    return (
      <motion.div
        className="brl brl--launch"
        role="status"
        aria-live="polite"
        aria-label={label ?? `Loading ${tenant.displayName} Workspace`}
        data-leaving={leaving || undefined}
      >
        <div className="brl__glow" aria-hidden />
        <div className="brl__stack">
          <motion.div
            initial={{ opacity: 0, scale: reduce ? 1 : 0.92, y: reduce ? 0 : 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.7, ease: EASE }}
          >
            <div className="brl__breathe">
              <LogoMark size={72} variant="gradient" title="" />
            </div>
          </motion.div>
          <motion.div
            className="brl__progress"
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.15 }}
          />
          <motion.div
            className="brl__chip"
            aria-hidden
            initial={{ opacity: 0, y: reduce ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.5, delay: reduce ? 0 : 0.25, ease: EASE }}
          >
            <TagChip>{tenant.displayName} Workspace</TagChip>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="brl brl--route"
      role="status"
      aria-live="polite"
      aria-label={label ?? "Loading"}
    >
      <motion.div
        className="brl__orbit"
        initial={{ opacity: 0, scale: reduce ? 1 : 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduce ? 0 : 0.35, ease: EASE }}
      >
        <span className="brl__ring" aria-hidden />
        <LogoMark size={44} variant="gradient" title="" />
      </motion.div>
    </div>
  );
}
